import React, { useRef, useState } from 'react';
import cn from 'classnames';

import NO_IMAGE from 'assets/images/slide-out/user-avatar.svg';
import filterActiveStylistsWithColumn from 'bl/data/filterActiveStylistsWithColumn.bl';
import Button from 'components/form/Button.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import TruncatedText from 'components/generic/TruncatedText.component';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { getActiveStylists } from 'utils/salons/getActiveStylists.util';
import { getAllStylists } from 'utils/salons/getAllStylists.util';

import HideDeletedStaff from 'scheduler/components/common/HideDeletedStaff.component';
import Checkbox from 'settings/page-elements/dropdown/Checkbox';

import CN from './StylistDropdown.module.scss';

const FLAG_DISPLAY_ELM = false;

const StylistDropdown = ({ stylists, dayView, displayStylists, onSelectStylist, selectedStylistId, hideInactive, showAllStylist, onClickAllStaffToday, show_only_active, trackEvent }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const dropdownRef = useRef(null);

  const [hideNonWorking, setHideNonWorking] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { val: isOpen, tip: toggle, nay: close } = useTip(false);

  useOnClickOutside(dropdownRef, close);

  const allStaff = getAllStylists(stylists);
  const activeStylists = getActiveStylists(stylists);

  const filteredList = allStaff.filter((s) => s.has_column && (searchQuery === '' || s.user_name.toLowerCase().includes(searchQuery.toLowerCase())));

  const selectedStylist = stylists.find((s) => s.id === selectedStylistId);
  const buttonName = activeStylists.length === 1 ? t('scheduler.stylistDropdown.weekView') : t('scheduler.stylistDropdown.allStaff');

  const activeStylistsWithColumn = filterActiveStylistsWithColumn(stylists);
  const isOneStylist = activeStylistsWithColumn.length === 1;
  const oneStylistButtonLabel = dayView ? t('scheduler.stylistDropdown.weekView') : t('scheduler.stylistDropdown.dayView');

  const handleSelect = (stylist) => {
    onSelectStylist(stylist);
    setSearchQuery('');
    close();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClickWeek = () => {
    toggle();
    trackEvent?.('view_week');
  };

  const handleShowHideNonWorking = () => {
    const newValue = !hideNonWorking;
    setHideNonWorking(newValue);
    if (newValue) {
      hideInactive();
    } else {
      showAllStylist(stylists);
    }
    close();
  };

  const handleSingleStylistToggle = () => {
    if (dayView) {
      // go to week view
      trackEvent?.('view_week');
      onSelectStylist(activeStylistsWithColumn[0]);
    } else {
      // from week to day
      onClickAllStaffToday();
    }
  };

  return (
    <div data-bem="StylistDropdown" className={cn(CN.component, { [CN.darkMode]: isDarkMode })} ref={dropdownRef}>
      <Button tag="StylistDropdown__button" variant="chameleon" width="auto" noMargin onClick={isOneStylist ? handleSingleStylistToggle : handleClickWeek} active={isOpen}>
        {isOneStylist ? (
          oneStylistButtonLabel
        ) : dayView ? (
          <span className={CN.buttonInnerSpace}>{buttonName}</span>
        ) : (
          <div className={CN.stylistButton}>
            <div
              className={cn(CN.profileButtonImgContainer, {
                [CN.empty]: !selectedStylist?.profile_image,
              })}
            >
              <img
                src={selectedStylist?.profile_image || NO_IMAGE}
                alt={selectedStylist?.user_name || 'Stylist'}
                className={cn(CN.stylistsImage, {
                  [CN.empty]: !selectedStylist?.profile_image,
                })}
              />
            </div>
            <TruncatedText max={9} suffix="..." value={selectedStylist?.user_name} />
          </div>
        )}
      </Button>

      {!isOneStylist && isOpen && (
        <DropdownFoldout variant="withAll" width="maxInTabletP" position="bottom">
          <DropdownFoldout.Header>
            <div className={CN.text}>{t('scheduler.stylistDropdown.selectStylist')}</div>
          </DropdownFoldout.Header>

          <DropdownFoldout.Body>
            <div className={CN.dropdownContent}>
              <input value={searchQuery} type="text" onChange={handleSearchChange} placeholder={t('scheduler.stylistDropdown.searchStaffPlaceholder')} className={CN.searchInput} autoFocus />

              <div
                onClick={() => {
                  onClickAllStaffToday();
                  close();
                }}
                className={cn(CN.item, { [CN.active]: !selectedStylistId })}
              >
                <div className={CN.profileImgContainer}>
                  <div className={cn(CN.avatar, CN.empty)}>
                    <div className={CN.itemCount}>{activeStylists.length}</div>
                  </div>
                </div>
                <div
                  className={cn(CN.itemText, {
                    [CN.shiftLeft]: !selectedStylistId,
                  })}
                >
                  {t('scheduler.stylistDropdown.todayStaff')}
                </div>
                <div className={CN.arrow}>{!selectedStylistId && <Icon variant="tick" width="xs" selected />}</div>
              </div>

              {filteredList.map((stylist) => {
                const isActive = (!dayView && stylist.id === selectedStylistId) || (show_only_active && dayView && !displayStylists.some((s) => s.id === stylist.id));

                return (
                  <div key={stylist.id} onClick={() => handleSelect(stylist)} className={cn(CN.item, { [CN.active]: isActive })}>
                    <div className={CN.profileImgContainer}>
                      <img
                        src={stylist?.profile_image || NO_IMAGE}
                        alt={stylist?.user_name}
                        className={cn(CN.avatar, {
                          [CN.empty]: !stylist?.profile_image,
                        })}
                      />
                    </div>
                    <div
                      className={cn(CN.itemText, {
                        [CN.shiftLeft]: isActive,
                      })}
                    >
                      {stylist.user_name}
                    </div>
                    <div className={CN.arrow}>{isActive && <Icon variant="tick" width="xs" selected />}</div>
                  </div>
                );
              })}
            </div>
          </DropdownFoldout.Body>

          <DropdownFoldout.Footer>
            <div className={CN.dropdownFooterContainer}>
              {FLAG_DISPLAY_ELM && (
                <div className="dropdown-item non-working" onClick={handleShowHideNonWorking}>
                  <label htmlFor="switchToDayView" style={{ display: 'inline' }}>
                    <Checkbox isChecked={hideNonWorking} onCheckboxClick={handleShowHideNonWorking} id="c1" />
                  </label>
                  <span className="list-item-name">{t('scheduler.stylistDropdown.hideNonWorking')}</span>
                </div>
              )}
              <HideDeletedStaff />
            </div>
          </DropdownFoldout.Footer>
        </DropdownFoldout>
      )}
    </div>
  );
};

export default StylistDropdown;
